import React, { useContext, useEffect, useState } from 'react';
import {
    PolarAngleAxis,
    PolarGrid,
    PolarRadiusAxis,
    Radar,
    RadarChart,
    ResponsiveContainer,
    Tooltip,
} from 'recharts';
import BaseReportBox, { LoadStatus } from "./BaseReportBox";
import { Report_getPopularSports } from "../../network/api/report.api";
import { ErrorContext } from "../../components/GympinPagesProvider";
import { Box, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";

export default function _SportRadar({ corporate }) {
    const error = useContext(ErrorContext);
    const [loadStatus, setLoadStatus] = useState(LoadStatus.LOADING);
    const [data, setData] = useState([]);

    useEffect(() => {
        getPopularSports();
    }, [corporate]);

    function getPopularSports() {
        setLoadStatus(LoadStatus.LOADING);
        if (!corporate?.Id) return;

        Report_getPopularSports({ id: corporate.Id }).then(result => {
            const rawList = result?.data?.Data || [];
            if (rawList.length > 0) {
                // مرتب‌سازی نزولی بر اساس بیشترین تعداد ورود به ورزش (بیشترین درصد)
                const formattedList = rawList
                    .map(item => ({
                        name: item.SportName || "نامشخص",
                        count: Number(item.SportEnteryCount) || 0,
                    }))
                    .sort((a, b) => b.count - a.count);

                let finalList = [];
                if (formattedList.length <= 5) {
                    finalList = formattedList;
                } else {
                    // ۵ ورزش اول با بیشترین مقدار
                    const top5 = formattedList.slice(0, 5);
                    // تجمیع مابقی ورزش‌ها در قالب «سایر ورزش‌ها»
                    const remaining = formattedList.slice(5);
                    const remainingSum = remaining.reduce((sum, curr) => sum + curr.count, 0);

                    finalList = [...top5];
                    if (remainingSum > 0) {
                        finalList.push({
                            name: "سایر ورزش‌ها",
                            count: remainingSum,
                        });
                    }
                }

                const radarData = finalList.map(item => ({
                    subject: item.name,
                    count: item.count,
                    fullMark: item.count,
                }));

                setData(radarData);
                setLoadStatus(LoadStatus.LOADED);
            } else {
                setLoadStatus(LoadStatus.NODATA);
            }
        }).catch(e => {
            try {
                setLoadStatus(LoadStatus.ERROR);
                error.showError({ message: e.response?.data?.Message || "خطای نامشخص" });
            } catch (f) {
                error.showError({ message: "خطای نامشخص" });
            }
        });
    }

    const totalCount = data.reduce((acc, curr) => acc + (curr.count || 0), 0);
    const maxCount = Math.max(...data.map(r => r.count || 0), 10);

    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            const val = payload[0].value || 0;
            const percent = totalCount > 0 ? Math.round((val / totalCount) * 100) : 0;
            return (
                <Box
                    sx={{
                        p: 1.2,
                        px: 1.8,
                        borderRadius: 2,
                        bgcolor: 'background.paper',
                        border: '1px solid',
                        borderColor: 'divider',
                        boxShadow: '0 4px 14px rgba(0,0,0,0.1)',
                        direction: 'rtl',
                        textAlign: 'right',
                    }}
                >
                    <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', display: 'block', mb: 0.3 }}>
                        {payload[0].payload.subject}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 800, color: 'primary.main' }}>
                        {val.toLocaleString('fa-IR')} ورود ({percent}٪)
                    </Typography>
                </Box>
            );
        }
        return null;
    };

    return (
        <BaseReportBox title={"گستره ورزشی شش ماهه"} loadStatus={loadStatus} ReloadData={getPopularSports}>
            <Grid sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', py: 1 }}>
                <ResponsiveContainer width="100%" height={320}>
                    <RadarChart cx="50%" cy="50%" outerRadius="75%" data={data}>
                        <PolarGrid stroke="rgba(128,128,128,0.2)" />
                        <PolarAngleAxis dataKey="subject" tick={{ fill: 'currentColor', fontSize: 12, fontWeight: 700 }} />
                        <PolarRadiusAxis
                            angle={data.length ? 360 / data.length : 60}
                            domain={[0, maxCount]}
                            tick={{ fill: 'rgba(128,128,128,0.7)', fontSize: 10 }}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Radar dataKey="count" stroke="#1c8b9c" fill="#1c8b9c" fillOpacity={0.5} strokeWidth={2} />
                    </RadarChart>
                </ResponsiveContainer>
            </Grid>
        </BaseReportBox>
    );
}
